import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Section1FilterIncidents from './grouping/Section1FilterIncidents';
import Section2HierarchicalAnalysis from './grouping/Section2HierarchicalAnalysis';
import Section3CrossGroupAi from './grouping/Section3CrossGroupAi';
import Section4ExportReport from './grouping/Section4ExportReport';
import Section5TicketRecords from './grouping/Section5TicketRecords';
import { PERIODS, QUICK_PROMPTS } from './grouping/groupingMockData';
import {
  buildQueryScopes,
  createShortlist,
  downloadGroupingExport,
  fetchGroupingFilters,
  runCrossGroupQuery,
  runHierarchicalGrouping,
} from './grouping/groupingApi';
import './grouping/grouping.css';

function GroupingRcaView({ dataSource, onConnectionChange }) {
  const [filterOptions, setFilterOptions] = useState({
    modules: ['All'],
    consultants: ['All'],
    periods: PERIODS,
  });
  const [filtersLoading, setFiltersLoading] = useState(true);

  const [macroArea, setMacroArea] = useState('All');
  const [consultant, setConsultant] = useState('All');
  const [fromPeriod, setFromPeriod] = useState('2026 02');
  const [toPeriod, setToPeriod] = useState('2026 02');
  const [similarityThreshold, setSimilarityThreshold] = useState(0.75);

  const [filtersApplied, setFiltersApplied] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [stats, setStats] = useState(null);

  const [groupingDone, setGroupingDone] = useState(false);
  const [isRunningGrouping, setIsRunningGrouping] = useState(false);
  const [runId, setRunId] = useState('');
  const [hierarchicalRows, setHierarchicalRows] = useState([]);
  const [ticketRecords, setTicketRecords] = useState([]);

  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');

  const periodOptions = useMemo(
    () => (filterOptions.periods?.length ? filterOptions.periods : PERIODS),
    [filterOptions.periods]
  );

  const queryScopes = useMemo(
    () => buildQueryScopes(hierarchicalRows),
    [hierarchicalRows]
  );

  const filterPayload = useMemo(
    () => ({
      dataSource,
      module: macroArea,
      consultant,
      fromPeriod,
      toPeriod,
      similarityThreshold,
    }),
    [dataSource, macroArea, consultant, fromPeriod, toPeriod, similarityThreshold]
  );

  const loadFilters = useCallback(async () => {
    setFiltersLoading(true);
    setError('');
    try {
      const data = await fetchGroupingFilters(dataSource);
      setFilterOptions({
        modules: data.modules?.length ? data.modules : ['All'],
        consultants: data.consultants?.length ? data.consultants : ['All'],
        periods: data.periods?.length ? data.periods : PERIODS,
      });
      onConnectionChange?.(true);
    } catch (err) {
      setError(err.message);
      onConnectionChange?.(false);
    } finally {
      setFiltersLoading(false);
    }
  }, [dataSource, onConnectionChange]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (!filterOptions.modules.includes(macroArea)) {
      setMacroArea(filterOptions.modules[0] || 'All');
    }
  }, [filterOptions.modules, macroArea]);

  useEffect(() => {
    if (!filterOptions.consultants.includes(consultant)) {
      setConsultant(filterOptions.consultants[0] || 'All');
    }
  }, [filterOptions.consultants, consultant]);

  useEffect(() => {
    if (!periodOptions.includes(fromPeriod)) {
      setFromPeriod(periodOptions[0] || '2026 02');
    }
    if (!periodOptions.includes(toPeriod)) {
      setToPeriod(periodOptions[periodOptions.length - 1] || '2026 02');
    }
  }, [periodOptions, fromPeriod, toPeriod]);

  const resetGroupingResults = () => {
    setGroupingDone(false);
    setRunId('');
    setHierarchicalRows([]);
    setTicketRecords([]);
  };

  const handleApplyFilters = async () => {
    setIsApplying(true);
    setError('');
    resetGroupingResults();
    try {
      const shortlist = await createShortlist(filterPayload);
      setStats(shortlist);
      setFiltersApplied(true);
      onConnectionChange?.(true);
    } catch (err) {
      setError(err.message);
      setFiltersApplied(false);
      setStats(null);
      onConnectionChange?.(false);
    } finally {
      setIsApplying(false);
    }
  };

  const handleRunGrouping = async () => {
    setIsRunningGrouping(true);
    setError('');
    resetGroupingResults();
    try {
      const result = await runHierarchicalGrouping(filterPayload);
      setRunId(result.runId);
      setHierarchicalRows(result.hierarchicalRows);
      setTicketRecords(result.ticketRecords);
      setGroupingDone(true);
      onConnectionChange?.(true);
    } catch (err) {
      setError(err.message);
      onConnectionChange?.(false);
    } finally {
      setIsRunningGrouping(false);
    }
  };

  const handleCrossGroupQuery = async ({ scope, userQuery }) => {
    if (!runId) throw new Error('Run hierarchical grouping first to obtain a session.');
    return runCrossGroupQuery({ runId, scope, userQuery });
  };

  const handleExport = async () => {
    if (!runId) {
      setError('Run hierarchical grouping first before exporting.');
      return;
    }
    setIsExporting(true);
    setError('');
    try {
      await downloadGroupingExport(runId);
      onConnectionChange?.(true);
    } catch (err) {
      setError(err.message);
      onConnectionChange?.(false);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="nlp-grouping-view">
      <div className="nlp-grouping-inner">
        {error && (
          <div className="nlp-grouping-error" role="alert">
            {error}
          </div>
        )}

        <Section1FilterIncidents
          macroArea={macroArea}
          consultant={consultant}
          fromPeriod={fromPeriod}
          toPeriod={toPeriod}
          similarityThreshold={similarityThreshold}
          moduleOptions={filterOptions.modules}
          consultantOptions={filterOptions.consultants}
          periodOptions={periodOptions}
          onMacroAreaChange={setMacroArea}
          onConsultantChange={setConsultant}
          onFromPeriodChange={setFromPeriod}
          onToPeriodChange={setToPeriod}
          onSimilarityChange={setSimilarityThreshold}
          onApply={handleApplyFilters}
          stats={stats}
          applied={filtersApplied}
          isApplying={isApplying}
          filtersLoading={filtersLoading}
        />

        <Section2HierarchicalAnalysis
          rows={hierarchicalRows}
          filtersApplied={filtersApplied}
          groupingDone={groupingDone}
          isRunning={isRunningGrouping}
          onRun={handleRunGrouping}
        />

        <Section3CrossGroupAi
          visible={groupingDone}
          queryScopes={queryScopes}
          quickPrompts={QUICK_PROMPTS}
          onQuery={handleCrossGroupQuery}
        />

        <Section4ExportReport
          visible={groupingDone}
          onExport={handleExport}
          isExporting={isExporting}
          disabled={!runId}
        />

        <Section5TicketRecords rows={ticketRecords} visible={groupingDone} />
      </div>
    </div>
  );
}

export default GroupingRcaView;
